import copy from '../../demo/fixtures/w5/copy.json'
import rules from '../../demo/fixtures/w5/rules.json'
import { useW5Live } from '../analysis/live'
import styles from './AutomationPage.module.css'

const CHART = { left: 44, right: 380, top: 12, bottom: 124 } as const

function pointX(index: number, count: number) {
  return CHART.left + index * (CHART.right - CHART.left) / (count - 1)
}

function trendDates(values: number[]) {
  const match = rules.pending.at.match(/(\d+)月(\d+)日/)
  const month = Number(match?.[1] ?? 1)
  const day = Number(match?.[2] ?? 1)
  const end = new Date(Date.UTC(2024, month - 1, day - 1))
  return values.map((_, index) => {
    const date = new Date(end)
    date.setUTCDate(end.getUTCDate() - (values.length - 1 - index))
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`
  })
}

export function BenchTrend() {
  const { automation } = useW5Live()
  const { benchmark } = automation
  const pointY = (value: number) => CHART.bottom - (value - benchmark.trend.lo) * (CHART.bottom - CHART.top) / (benchmark.trend.hi - benchmark.trend.lo)
  const linePath = (values: number[]) => values.map((value, index) => `${index === 0 ? 'M' : 'L'}${pointX(index, values.length).toFixed(1)} ${pointY(value).toFixed(1)}`).join(' ')
  const ticks = Array.from({ length: benchmark.trend.hi - benchmark.trend.lo + 1 }, (_, index) => benchmark.trend.lo + index)
  const dates = trendDates(benchmark.trend.you)
  const youPath = linePath(benchmark.trend.you)
  const marketPath = linePath(benchmark.trend.mkt)
  const areaPath = `${youPath} L${CHART.right} ${CHART.bottom} L${CHART.left} ${CHART.bottom} Z`
  const costRow = benchmark.rows.find((row) => row.metric === benchmark.trend.title.split(' · ')[0])

  return (
    <section className={styles.benchTrend} data-scope="bench-trend" lang="zh-CN">
      <div className={styles.sectionHeading}>
        <p>{copy.sections.benchTrend}</p>
        <span>{copy.sections.benchTrendHint}</span>
      </div>
      <div className={styles.chart}>
        <div className={styles.chartTitle}>
          <strong>{benchmark.trend.title}</strong>
          <span>{copy.sections.benchmarkHint.split(' · ').at(-1)}</span>
        </div>
        <svg viewBox="0 0 392 150" width="392" height="150" aria-label={benchmark.trend.title} role="img">
          {ticks.map((tick) => {
            const y = pointY(tick)
            return (
              <g key={tick}>
                <line x1={CHART.left} x2={CHART.right} y1={y} y2={y} stroke="var(--divider)" strokeWidth="1" />
                <text x="38" y={y + 4} textAnchor="end" fontSize="10.5" fill="var(--text-3)">${tick}</text>
              </g>
            )
          })}
          {dates.map((date, index) => (
            <text key={date} x={pointX(index, dates.length)} y="142" textAnchor="middle" fontSize="10.5" fill="var(--text-3)">{date}</text>
          ))}
          <path d={marketPath} fill="none" stroke="var(--text-3)" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" />
          <path d={areaPath} fill="var(--ai-blush)" opacity=".8" />
          <path d={youPath} fill="none" stroke="var(--ai-lilac)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          {benchmark.trend.you.map((value, index) => (
            <circle key={`${value}-${index}`} cx={pointX(index, benchmark.trend.you.length)} cy={pointY(value)} r="3.2" fill="var(--ai-lilac)" stroke="var(--surface)" strokeWidth="1.5" />
          ))}
        </svg>
        <div className={styles.legend}>
          <span><i />你 {costRow?.you}（+{automation.yourChange}%）</span>
          <span><i className={styles.dashedLegend} />大盘中位 {costRow?.median}（+{automation.marketChange}%）</span>
        </div>
      </div>
      <p className={styles.trendNote}>同类目千次成本一周涨了 {automation.marketChange}%，你的只涨 {automation.yourChange}%——因为我在 9月2日 把预算向低成本时段倾斜了（R6），没有跟着大盘追高。</p>
    </section>
  )
}
